import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Search as SearchIcon, Syringe, AlertTriangle } from "lucide-react";
import Paywall from "../components/Paywall.jsx";
import { useIsPremium } from "../lib/premium";

export default function AntibioticGuide() {
  const premium = useIsPremium();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");

  useEffect(() => {
    if (!premium) return;
    const params = {};
    if (q) params.q = q;
    if (cat) params.category = cat;
    api.get("/antibiotic-guide", { params }).then((r) => setItems(r.data)).catch(() => {});
  }, [q, cat, premium]);

  const cats = useMemo(() => [...new Set(items.map((i) => i.category))].filter(Boolean), [items]);

  if (!premium) return <Paywall feature="The Antibiotic Guide" />;

  return (
    <div className="space-y-6" data-testid="abx-page">
      <div>
        <div className="text-xs tracking-widest uppercase text-slate-500 font-semibold">Empirical</div>
        <div className="flex items-center justify-between flex-wrap gap-2 mt-1">
          <h1 className="font-heading text-3xl font-bold text-[#111827] flex items-center gap-2">
            <Syringe className="w-7 h-7 text-[#111827]" /> Antibiotic Guide
          </h1>
          <Badge className="bg-slate-100 text-slate-700 border-0">{items.length} conditions</Badge>
        </div>
        <p className="text-slate-600 text-sm mt-1">First-line and alternative empirical regimens by condition.</p>
      </div>

      <div className="relative max-w-lg">
        <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search condition..." className="pl-9 h-11 bg-white" data-testid="abx-search" />
      </div>

      {cats.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          <button onClick={() => setCat("")} className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap ${!cat ? "bg-[#0B5FFF] text-white" : "bg-white border border-slate-200 text-slate-700"}`}>All</button>
          {cats.map((c) => (
            <button key={c} onClick={() => setCat(c === cat ? "" : c)} className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap ${cat === c ? "bg-[#0B5FFF] text-white" : "bg-white border border-slate-200 text-slate-700"}`}>{c}</button>
          ))}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {items.map((g) => (
          <Card key={g.guide_id} className="border-slate-200" data-testid={`abx-card-${g.guide_id}`}>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-1">
                <Badge className="bg-[#EFF4FF] text-[#111827] border-0 text-[10px]">{g.category}</Badge>
                {g.setting && <span className="text-xs text-slate-500">{g.setting}</span>}
              </div>
              <h3 className="font-heading font-semibold text-lg text-[#111827]">{g.condition}</h3>

              {Array.isArray(g.first_line) && g.first_line.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs tracking-widest uppercase text-emerald-700 font-semibold mb-1">First-line</div>
                  {g.first_line.map((r, i) => (
                    <div key={i} className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-md text-sm mb-1">
                      <div className="font-semibold text-emerald-900">{r.drug}</div>
                      <div className="text-emerald-800">{r.dose} {r.duration ? `× ${r.duration}` : ""}</div>
                      {r.notes && <div className="text-xs text-emerald-700/80 mt-0.5">{r.notes}</div>}
                    </div>
                  ))}
                </div>
              )}

              {Array.isArray(g.alternative) && g.alternative.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs tracking-widest uppercase text-slate-500 font-semibold mb-1">Alternative</div>
                  {g.alternative.map((r, i) => (
                    <div key={i} className="p-2.5 bg-slate-50 border border-slate-200 rounded-md text-sm mb-1">
                      <div className="font-semibold text-[#111827]">{r.drug}</div>
                      <div className="text-slate-700">{r.dose} {r.duration ? `× ${r.duration}` : ""}</div>
                      {r.notes && <div className="text-xs text-slate-500 mt-0.5">{r.notes}</div>}
                    </div>
                  ))}
                </div>
              )}

              {Array.isArray(g.red_flags) && g.red_flags.length > 0 && (
                <div className="mt-3 p-2.5 bg-rose-50 border border-rose-200 rounded-md text-xs text-rose-800">
                  <div className="flex items-center gap-1 font-semibold mb-1"><AlertTriangle className="w-3 h-3" /> Red flags</div>
                  <div>{g.red_flags.join(" · ")}</div>
                </div>
              )}

              {g.pregnancy && (
                <div className="mt-3 p-2.5 bg-pink-50 border border-pink-200 rounded-md text-xs text-pink-900" data-testid={`abx-pregnancy-${g.guide_id}`}>
                  <div className="font-semibold mb-1">In pregnancy</div>
                  <div>{g.pregnancy}</div>
                  <div className="mt-1.5 pt-1.5 border-t border-pink-200 text-[11px] italic text-pink-700" data-testid={`abx-pregnancy-disclaimer-${g.guide_id}`}>
                    Always verify antibiotic safety in pregnancy and consult a specialist before prescribing.
                  </div>
                </div>
              )}

              {g.key_notes && <p className="text-xs text-slate-600 mt-3 italic">{g.key_notes}</p>}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
